import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router"
import { useGetBudgetById } from "~/hooks/useBudgets"
import { Module } from "~/components/design-system/pages"
import { type HeaderTab } from "~/components/reusable/HeaderTabs"
import { BreadcrumbWrapper } from "~/components/reusable/Breadcrumb"
import LoadingOverlay from "~/components/reusable/LoadingOverlay"
import NotFound from "~/components/reusable/NotFound"
import {
  PencilSimple,
  ChartLine,
  Trash,
  ChartPie,
  CurrencyCircleDollar,
  Sliders,
  TagChevron,
} from "@phosphor-icons/react"
import { ActionButton } from "~/components/reusable/ActionButton"
import { BudgetProvider } from "~/context/BudgetProvider"
import { DeleteBudgetDialog } from "~/components/budget/DeleteBudgetDialog"
import { BudgetCreateEditModal } from "~/components/budget/BudgetCreateEditModal"
import { ComparableBudgetSelector } from "~/components/budget/ComparableBudgetSelector"
import { useFlags } from "~/features/feature-flags"
import { BudgetSyncWarningContainer } from "~/components/reusable/BudgetSyncWarningContainer"
import { useRecordRecentlyViewed } from "~/hooks/useRecentlyViewed"
import { formatFiscalYear } from "~/utils/fiscalCalendar"

export const Route = createFileRoute("/_auth/budgets/_layout/$fiscalYear/$budgetId/_layout")({
  component: RouteComponent,
})

enum BudgetTabs {
  budget = "Target",
  allocationPlanning = "Seasonality Planning",
  markdownPlanning = "Markdown Planning",
  budgetReport = "Target Report",
  salesPrices = "Sales Prices",
}

function RouteComponent() {
  const { budgetId, fiscalYear } = Route.useParams()
  const { budgetEstimatedSalesPrice, budgetUnreleased } = useFlags()
  const navigate = useNavigate()

  const { data: budget, isLoading } = useGetBudgetById(parseInt(budgetId))

  const groupId = budget?.collectionGroupId ?? null

  const tabs: HeaderTab[] = [
    {
      label: BudgetTabs.budget,
      path: "/budgets/$fiscalYear/$budgetId/target",
      icon: <ChartPie />,
    },
    {
      label: BudgetTabs.allocationPlanning,
      path: "/budgets/$fiscalYear/$budgetId/allocation-planning",
      icon: <Sliders />,
    },
    {
      label: BudgetTabs.markdownPlanning,
      path: "/budgets/$fiscalYear/$budgetId/markdown-planning",
      icon: <TagChevron />,
    },
    ...(budgetUnreleased
      ? [
          {
            label: BudgetTabs.budgetReport,
            path: "/budgets/$fiscalYear/$budgetId/target-report",
            icon: <ChartLine />,
          } satisfies HeaderTab,
        ]
      : []),
    ...(budgetEstimatedSalesPrice
      ? [
          {
            label: BudgetTabs.salesPrices,
            path: "/budgets/$fiscalYear/$budgetId/sales-prices",
            icon: <CurrencyCircleDollar />,
          } as HeaderTab,
        ]
      : []),
  ]

  useRecordRecentlyViewed(
    budget
      ? { type: "budget", route: `/budgets/${fiscalYear}/${budgetId}`, label: budget.name }
      : null,
  )

  if (isLoading) return <LoadingOverlay />
  if (budget === undefined) return <NotFound />

  const breadcrumbItems = [
    { label: "Budgets", href: "/budgets" },
    { label: formatFiscalYear(fiscalYear), href: `/budgets/${fiscalYear}` },
    { label: "Collections", href: `/budgets/${fiscalYear}/collections` },
    { label: budget.name },
  ]

  return (
    <BudgetProvider budget={budget}>
      <Module
        breadcrumb={<BreadcrumbWrapper items={breadcrumbItems} />}
        actions={
          <>
            <ComparableBudgetSelector />
            <BudgetSyncWarningContainer groupId={groupId} />
            <BudgetCreateEditModal
              key={budgetId}
              existingBudget={budget}
              customTrigger={
                <ActionButton size="sm" action={{ name: "Edit target", icon: <PencilSimple /> }} />
              }
            />
            <DeleteBudgetDialog
              budgetId={parseInt(budgetId)}
              budgetName={budget?.name || ""}
              onDelete={() =>
                navigate({ to: "/budgets/$fiscalYear/collections", params: { fiscalYear } })
              }
              customTrigger={
                <ActionButton size="sm" action={{ name: "Delete target", icon: <Trash /> }} />
              }
            />
          </>
        }
        tabs={{ items: tabs, path: "/budgets/$fiscalYear/$budgetId" }}
      >
        <Outlet />
      </Module>
    </BudgetProvider>
  )
}
